import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MilestoneService, {
  type MilestoneListFilters,
  type CreateMilestonePayload,
} from '../../../../AppSettings/services/MilestoneService';
import EmployeeService from '../../../../AppSettings/services/EmployeeService';
import ToastService from '../../../../AppSettings/services/ToastService';
import './MilestonesPage.css';

/**
 * Project Milestones Experience Page
 * 
 * PRD2: Project Milestones Experience
 * This page provides the user interface for managing workflow milestones within project details.
 * 
 * Features:
 * - View milestones in a table with status indicators, dates, and assignments
 * - Edit milestones inline (dates, status) without navigating away
 * - Filter and search milestones by status, discipline, owner, or keyword
 * - Audit changes with a complete history of who changed what and when
 * - Enforce permissions so only authorized users can edit based on their role
 * - Add custom milestones for project-specific requirements
 */

interface FilterState {
  status: string[];
  discipline: string[];
  category: string[];
  owner: string[];
  search: string;
}

const MilestonesPage: React.FC = () => {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const projectIdFromQuery = new URLSearchParams(window.location.search).get('projectId') || undefined;
  const projectId = routeProjectId || projectIdFromQuery || '';
  
  const queryClient = useQueryClient();
  
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    discipline: [],
    category: [],
    owner: [],
    search: '',
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAuditDrawer, setShowAuditDrawer] = useState(false);
  const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState<string | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<{
    id: string;
    field: 'plannedDate' | 'actualDate' | 'status' | 'notes';
    value: string;
  } | null>(null);
  const [permissionRole, setPermissionRole] = useState('');
  
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [sortBy] = useState('plannedDate');
  const [sortOrder] = useState<'asc' | 'desc'>('asc');

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  React.useEffect(() => {
    setFilters(prev => ({ ...prev, search: debouncedSearch }));
    setPage(1);
  }, [debouncedSearch]);

  const queryFilters: MilestoneListFilters = useMemo(() => ({
    status: filters.status.length > 0 ? filters.status : undefined,
    discipline: filters.discipline.length > 0 ? filters.discipline : undefined,
    category: filters.category.length > 0 ? filters.category : undefined,
    owner: filters.owner.length > 0 ? filters.owner : undefined,
    search: filters.search || undefined,
    page,
    limit,
    sortBy,
    sortOrder,
  }), [filters, page, limit, sortBy, sortOrder]);

  const {
    data: milestonesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['milestones', projectId, queryFilters],
    queryFn: () => MilestoneService.getProjectMilestones(projectId, queryFilters),
    enabled: !!projectId,
    staleTime: 30000,
  });

  const {
    data: auditData,
    isLoading: isLoadingAudit,
  } = useQuery({
    queryKey: ['milestone-audit', projectId, selectedMilestoneId],
    queryFn: () => {
      if (!selectedMilestoneId) return null;
      return MilestoneService.getMilestoneAudit(projectId, selectedMilestoneId, 1, 50, 'desc');
    },
    enabled: !!selectedMilestoneId && showAuditDrawer,
  });

  const updateMutation = useMutation({
    mutationFn: ({ milestoneId, payload }: { milestoneId: string; payload: any }) =>
      MilestoneService.updateMilestone(projectId, milestoneId, payload),
    onSuccess: (response) => {
      if (response.successful && response.data) {
        ToastService.success('Milestone updated successfully');
        queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
        setEditingMilestone(null);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.data?.error || error?.message || 'Failed to update milestone';
      const errorCode = error?.data?.errorCode;
      
      if (errorCode === 'PERMISSION_DENIED') {
        ToastService.error(`Permission denied: ${errorMessage}`);
      } else {
        ToastService.error(errorMessage);
      }
      
      if (editingMilestone) {
        setEditingMilestone(null);
        refetch();
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateMilestonePayload) =>
      MilestoneService.createMilestone(projectId, payload),
    onSuccess: (response) => {
      if (response.successful && response.data) {
        ToastService.success('Milestone created successfully');
        queryClient.invalidateQueries({ queryKey: ['milestones', projectId] });
        setShowAddModal(false);
        resetAddForm();
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.data?.error || error?.message || 'Failed to create milestone';
      const errorCode = error?.data?.errorCode;
      
      if (errorCode === 'PERMISSION_DENIED') {
        ToastService.error(`Permission denied: ${errorMessage}`);
      } else {
        ToastService.error(errorMessage);
      }
    },
  });

  const createPermissionMutation = useMutation({
    mutationFn: (role: string) =>
      EmployeeService.createPermission({ role }),
    onSuccess: (response) => {
      ToastService.success(`Permission "${response.role}" created successfully`);
      setShowAddPermissionModal(false);
      setPermissionRole('');
      queryClient.invalidateQueries({ queryKey: ['permissions'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.data?.error || error?.message || 'Failed to create permission';
      ToastService.error(errorMessage);
    },
  });

  const toggleFilter = useCallback((type: keyof FilterState, value: string) => {
    setFilters(prev => {
      const current = prev[type];
      const isArray = Array.isArray(current);
      const array = isArray ? current : [];
      
      if (type === 'search') {
        return { ...prev, search: value };
      }
      
      const newArray = array.includes(value)
        ? array.filter(v => v !== value)
        : [...array, value];
      
      return { ...prev, [type]: newArray };
    });
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      status: [],
      discipline: [],
      category: [],
      owner: [],
      search: '',
    });
    setSearchInput('');
    setPage(1);
  }, []);

  const startEdit = useCallback((milestoneId: string, field: 'plannedDate' | 'actualDate' | 'status' | 'notes', currentValue: string) => {
    setEditingMilestone({ id: milestoneId, field, value: currentValue || '' });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMilestone(null);
  }, []);

  const saveEdit = useCallback(() => {
    if (!editingMilestone) return;
    
    const payload: any = {};
    if (editingMilestone.field === 'plannedDate' || editingMilestone.field === 'actualDate') {
      payload[editingMilestone.field] = editingMilestone.value || null;
    } else if (editingMilestone.field === 'status') {
      payload.status = editingMilestone.value;
    } else if (editingMilestone.field === 'notes') {
      payload.notes = editingMilestone.value || null;
    }

    updateMutation.mutate({
      milestoneId: editingMilestone.id,
      payload,
    });
  }, [editingMilestone, updateMutation]);

  const openAuditDrawer = useCallback((milestoneId: string) => {
    setSelectedMilestoneId(milestoneId);
    setShowAuditDrawer(true);
  }, []);

  const closeAuditDrawer = useCallback(() => {
    setShowAuditDrawer(false);
    setSelectedMilestoneId(null);
  }, []);

  const handleExportAudit = useCallback(async () => {
    if (!selectedMilestoneId) return;
    try {
      await MilestoneService.exportMilestoneAuditCSV(projectId, selectedMilestoneId);
      ToastService.success('Audit log exported successfully');
    } catch (error: any) {
      ToastService.error(`Failed to export audit log: ${error?.message || 'Unknown error'}`);
    }
  }, [projectId, selectedMilestoneId]);

  const [addForm, setAddForm] = useState<CreateMilestonePayload>({
    name: '',
    category: '',
    discipline: '',
    role: '',
    plannedDate: '',
    dependencies: [],
    description: '',
  });

  const resetAddForm = useCallback(() => {
    setAddForm({
      name: '',
      category: '',
      discipline: '',
      role: '',
      plannedDate: '',
      dependencies: [],
      description: '',
    });
  }, []);

  const handleCreateMilestone = useCallback(() => {
    if (!addForm.name.trim()) {
      ToastService.warning('Milestone name is required');
      return;
    }

    createMutation.mutate(addForm);
  }, [addForm, createMutation]);

  const statusOptions = ['planned', 'in_progress', 'blocked', 'completed'];

  const milestones = milestonesData?.data?.milestones || [];
  const pagination = milestonesData?.data?.pagination;
  const auditEntries = auditData?.data?.entries || [];

  const getStatusBadgeClass = (status: string, isOverdue: boolean) => {
    if (isOverdue && status !== 'completed') {
      return 'status-badge status-overdue';
    }
    return `status-badge status-${status}`;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planned: 'Planned',
      in_progress: 'In Progress',
      blocked: 'Blocked',
      completed: 'Completed',
    };
    return labels[status] || status;
  };

  const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const formatDateForDisplay = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="milestones-page">
      <div className="milestones-header">
        <div className="header-content">
          <h1>Project Milestones</h1>
          <p className="page-description">
            Manage workflow milestones, track progress, and monitor deadlines for your projects.
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAddPermissionModal(true)}
            title="Add a new permission role"
          >
            Add Permission
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            Add Milestone
          </button>
        </div>
      </div>

      {(filters.status.length > 0 || filters.discipline.length > 0 || 
        filters.category.length > 0 || filters.owner.length > 0 || filters.search) && (
        <div className="active-filters">
          <span className="filter-label">Active filters:</span>
          {filters.status.map(status => (
            <span key={`status-${status}`} className="filter-pill active">
              Status: {getStatusLabel(status)}
              <button onClick={() => toggleFilter('status', status)}>×</button>
            </span>
          ))}
          {filters.discipline.map(discipline => (
            <span key={`discipline-${discipline}`} className="filter-pill active">
              Discipline: {discipline}
              <button onClick={() => toggleFilter('discipline', discipline)}>×</button>
            </span>
          ))}
          {filters.category.map(category => (
            <span key={`category-${category}`} className="filter-pill active">
              Category: {category}
              <button onClick={() => toggleFilter('category', category)}>×</button>
            </span>
          ))}
          {filters.owner.map(owner => (
            <span key={`owner-${owner}`} className="filter-pill active">
              Owner: {owner}
              <button onClick={() => toggleFilter('owner', owner)}>×</button>
            </span>
          ))}
          {filters.search && (
            <span className="filter-pill active">
              Search: {filters.search}
              <button onClick={() => { setSearchInput(''); toggleFilter('search', ''); }}>×</button>
            </span>
          )}
          <button className="btn-link" onClick={clearFilters}>Clear all</button>
        </div>
      )}

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-content">
            <h3>Filter Milestones</h3>
            <div className="filter-group">
              <label>Status</label>
              <div className="filter-pills">
                {statusOptions.map(status => (
                  <span
                    key={status}
                    className={`filter-pill ${filters.status.includes(status) ? 'active' : ''}`}
                    onClick={() => toggleFilter('status', status)}
                  >
                    {getStatusLabel(status)}
                  </span>
                ))}
              </div>
            </div>
            <div className="filter-group">
              <label>Discipline</label>
              <div className="filter-pills">
                {['mechanical', 'electrical', 'civil', 'structural'].map(discipline => (
                  <span
                    key={discipline}
                    className={`filter-pill ${filters.discipline.includes(discipline) ? 'active' : ''}`}
                    onClick={() => toggleFilter('discipline', discipline)}
                  >
                    {discipline.charAt(0).toUpperCase() + discipline.slice(1)}
                  </span>
                ))}
              </div>
            </div>
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search milestones..."
                className="search-input"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="milestones-table-container">
        {isLoading ? (
          <div className="loading-state">Loading milestones...</div>
        ) : error ? (
          <div className="error-state">
            <p>Error loading milestones: {error instanceof Error ? error.message : 'Unknown error'}</p>
            <button className="btn btn-primary" onClick={() => refetch()}>Retry</button>
          </div>
        ) : milestones.length === 0 ? (
          <div className="empty-state">
            <p>No milestones found. Create your first milestone to get started.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              Add Milestone
            </button>
          </div>
        ) : (
          <>
            <table className="milestones-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Discipline</th>
                  <th>Planned Date</th>
                  <th>Actual Date</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((milestone) => (
                  <tr key={milestone.milestoneId}>
                    <td>{milestone.name}</td>
                    <td>{milestone.category || '—'}</td>
                    <td>{milestone.discipline || '—'}</td>
                    <td>
                      {editingMilestone?.id === milestone.milestoneId && editingMilestone?.field === 'plannedDate' ? (
                        <div className="inline-edit">
                          <input
                            type="date"
                            value={formatDateForInput(editingMilestone.value)}
                            onChange={(e) => {
                              if (editingMilestone) {
                                setEditingMilestone({ 
                                  id: editingMilestone.id,
                                  field: editingMilestone.field,
                                  value: e.target.value 
                                });
                              }
                            }}
                            className="date-input"
                            autoFocus
                          />
                          <div className="edit-actions">
                            <button className="btn-icon" onClick={saveEdit} title="Save">✓</button>
                            <button className="btn-icon" onClick={cancelEdit} title="Cancel">✕</button>
                          </div>
                        </div>
                      ) : (
                        <span
                          className="editable-cell"
                          onClick={() => startEdit(milestone.milestoneId, 'plannedDate', milestone.plannedDate || '')}
                          title="Click to edit"
                        >
                          {formatDateForDisplay(milestone.plannedDate)}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingMilestone?.id === milestone.milestoneId && editingMilestone?.field === 'actualDate' ? (
                        <div className="inline-edit">
                          <input
                            type="date"
                            value={formatDateForInput(editingMilestone.value)}
                            onChange={(e) => {
                              if (editingMilestone) {
                                setEditingMilestone({ 
                                  id: editingMilestone.id,
                                  field: editingMilestone.field,
                                  value: e.target.value 
                                });
                              }
                            }}
                            className="date-input"
                            autoFocus
                          />
                          <div className="edit-actions">
                            <button className="btn-icon" onClick={saveEdit} title="Save">✓</button>
                            <button className="btn-icon" onClick={cancelEdit} title="Cancel">✕</button>
                          </div>
                        </div>
                      ) : (
                        <span
                          className="editable-cell"
                          onClick={() => startEdit(milestone.milestoneId, 'actualDate', milestone.actualDate || '')}
                          title="Click to edit"
                        >
                          {formatDateForDisplay(milestone.actualDate)}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingMilestone?.id === milestone.milestoneId && editingMilestone?.field === 'status' ? (
                        <div className="inline-edit">
                          <select
                            value={editingMilestone.value}
                            onChange={(e) => {
                              if (editingMilestone) {
                                setEditingMilestone({ 
                                  id: editingMilestone.id,
                                  field: editingMilestone.field,
                                  value: e.target.value 
                                });
                              }
                            }}
                            className="status-select"
                            autoFocus
                          >
                            {statusOptions.map(opt => {
                              const canChange = 
                                (opt === 'completed' && milestone.actions.canMarkCompleted) ||
                                (opt === 'blocked' && milestone.actions.canMarkBlocked) ||
                                (opt === 'in_progress' && milestone.actions.canMarkInProgress) ||
                                opt === milestone.status;
                              return (
                                <option key={opt} value={opt} disabled={!canChange}>
                                  {getStatusLabel(opt)}
                                </option>
                              );
                            })}
                          </select>
                          <div className="edit-actions">
                            <button className="btn-icon" onClick={saveEdit} title="Save">✓</button>
                            <button className="btn-icon" onClick={cancelEdit} title="Cancel">✕</button>
                          </div>
                        </div>
                      ) : (
                        <span
                          className={getStatusBadgeClass(milestone.status, milestone.isOverdue)}
                          onClick={() => {
                            const canEdit = milestone.actions.canMarkCompleted || 
                                          milestone.actions.canMarkBlocked || 
                                          milestone.actions.canMarkInProgress;
                            if (canEdit) {
                              startEdit(milestone.milestoneId, 'status', milestone.status);
                            }
                          }}
                          style={{ 
                            cursor: (milestone.actions.canMarkCompleted || milestone.actions.canMarkBlocked || milestone.actions.canMarkInProgress) 
                              ? 'pointer' : 'not-allowed',
                            opacity: (milestone.actions.canMarkCompleted || milestone.actions.canMarkBlocked || milestone.actions.canMarkInProgress) 
                              ? 1 : 0.6
                          }}
                          title={
                            (milestone.actions.canMarkCompleted || milestone.actions.canMarkBlocked || milestone.actions.canMarkInProgress)
                              ? "Click to edit"
                              : "You don't have permission to change the status"
                          }
                        >
                          {getStatusLabel(milestone.status)}
                        </span>
                      )}
                    </td>
                    <td>{milestone.ownerRole || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="View Audit"
                          onClick={() => openAuditDrawer(milestone.milestoneId)}
                        >
                          📋
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {pagination && pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showAuditDrawer && selectedMilestoneId && (
        <div className="drawer-overlay" onClick={closeAuditDrawer}>
          <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h2>Audit Log</h2>
              <div className="drawer-actions">
                <button className="btn btn-secondary" onClick={handleExportAudit}>
                  Export CSV
                </button>
                <button className="btn-icon" onClick={closeAuditDrawer}>✕</button>
              </div>
            </div>
            <div className="drawer-body">
              {isLoadingAudit ? (
                <div className="loading-state">Loading audit log...</div>
              ) : auditEntries.length === 0 ? (
                <div className="empty-state">No audit entries found.</div>
              ) : (
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Field</th>
                      <th>Old Value</th>
                      <th>New Value</th>
                      <th>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEntries.map((entry) => (
                      <tr key={entry.auditId}>
                        <td>{formatDateForDisplay(entry.changeTimestamp)}</td>
                        <td>{entry.fieldName}</td>
                        <td>{entry.oldValue || '—'}</td>
                        <td>{entry.newValue || '—'}</td>
                        <td>{entry.actor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetAddForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Milestone</h2>
              <button
                className="btn-icon"
                onClick={() => { setShowAddModal(false); resetAddForm(); }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Milestone name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={addForm.category}
                  onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                  className="form-input"
                >
                  <option value="">Select category</option>
                  <option value="review">Review</option>
                  <option value="approval">Approval</option>
                  <option value="documentation">Documentation</option>
                  <option value="design">Design</option>
                </select>
              </div>
              <div className="form-group">
                <label>Discipline</label>
                <select
                  value={addForm.discipline}
                  onChange={(e) => setAddForm({ ...addForm, discipline: e.target.value })}
                  className="form-input"
                >
                  <option value="">Select discipline</option>
                  <option value="mechanical">Mechanical</option>
                  <option value="electrical">Electrical</option>
                  <option value="civil">Civil</option>
                  <option value="structural">Structural</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assigned Role</label>
                <input
                  type="text"
                  value={addForm.role}
                  onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                  placeholder="e.g., project_manager"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Planned Date</label>
                <input
                  type="date"
                  value={addForm.plannedDate}
                  onChange={(e) => setAddForm({ ...addForm, plannedDate: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={addForm.description}
                  onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                  placeholder="Milestone description or notes"
                  className="form-input"
                  rows={3}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowAddModal(false); resetAddForm(); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateMilestone}
                disabled={createMutation.isPending || !addForm.name.trim()}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Milestone'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPermissionModal && (
        <div className="modal-overlay" onClick={() => { setShowAddPermissionModal(false); setPermissionRole(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Permission</h2>
              <button
                className="btn-icon"
                onClick={() => { setShowAddPermissionModal(false); setPermissionRole(''); }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  value={permissionRole}
                  onChange={(e) => setPermissionRole(e.target.value)}
                  placeholder="e.g., project_manager, team_member, viewer"
                  className="form-input"
                  autoFocus
                />
                <small className="form-hint">
                  Common roles: project_manager, matter_manager, discipline_lead, team_member, reviewer, approver, document_control, admin, viewer
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowAddPermissionModal(false); setPermissionRole(''); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (permissionRole.trim()) {
                    createPermissionMutation.mutate(permissionRole.trim());
                  }
                }}
                disabled={createPermissionMutation.isPending || !permissionRole.trim()}
              >
                {createPermissionMutation.isPending ? 'Creating...' : 'Create Permission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default MilestonesPage;
